
import { Collection } from "./types";

/**
 * Ensures a collection has a valid name
 * If name is missing or empty, creates a fallback name using the collection ID
 */
export const ensureValidCollectionName = (collection: any): Collection => {
  // Handle null or undefined collection
  if (!collection) {
    console.error("Attempted to process a null/undefined collection");
    return {
      id: "unknown",
      name: "Unknown Collection",
      description: null,
      icon: null,
      color: "#ccc",
      created_at: new Date().toISOString(),
      user_id: "unknown",
    } as Collection;
  }
  
  // Deep copy to avoid modifying the original
  const processedCollection = { ...collection };
  
  // If name is missing or empty, create a fallback name
  if (!processedCollection.name || typeof processedCollection.name !== 'string' || processedCollection.name.trim() === '') {
    console.warn(`Collection with ID ${processedCollection.id || 'unknown'} has empty name - applying default name`);
    processedCollection.name = `Collection ${processedCollection.id ? processedCollection.id.substring(0, 4) : 'New'}`;
  }
  
  // Ensure other required properties exist
  if (!processedCollection.id) processedCollection.id = "unknown";
  if (!processedCollection.user_id) processedCollection.user_id = "unknown";
  if (!processedCollection.created_at) processedCollection.created_at = new Date().toISOString();
  if (!processedCollection.color) processedCollection.color = "#ccc";
  
  return processedCollection as Collection;
};

/**
 * Get a display name for a collection (partial or complete)
 */
export const getCollectionDisplayName = (
  collection: Partial<Collection> | null, 
  getFullCollection?: (id: string) => Collection | undefined,
  collections?: Collection[]
): string => {
  // If collection is null or undefined, return empty string
  if (!collection) return "Missing Collection";
  
  // If we have a name, use it directly
  if (collection.name && typeof collection.name === 'string' && collection.name.trim() !== '') {
    return collection.name;
  }
  
  // If we have an ID but not a name, try to look up the full collection
  if (collection.id && getFullCollection) {
    const fullCollection = getFullCollection(collection.id);
    if (fullCollection && fullCollection.name) {
      return fullCollection.name;
    }
  }
  
  // If we have an ID, use it to create a fallback name
  if (collection.id) {
    return `Collection ${collection.id.substring(0, 4)}`;
  }
  
  // For completely empty objects, return a generic name
  return "New Collection";
};
